import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { AppAction } from '../../store/actions';
import { AppLockModal } from '../AppLockModal';
import { DEFAULT_LOCK_SECONDS } from '../../config/constants';

/**
 * What counts as the user being present.
 *
 * `mousemove` and `keydown` alone meant a touch device registered no activity at
 * all — someone reading and scrolling on a tablet was idle by this measure and
 * got locked out mid-use.
 */
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'pointerdown', 'touchstart', 'scroll', 'wheel'];

/**
 * Floor between activity signals that re-arm the timer.
 *
 * `mousemove` fires at pointer sample rate, and the handler used to run
 * `clearTimeout` + `setTimeout` on every one of them — hundreds of timer
 * allocations per second of movement, competing with the message stream for the
 * main thread. For an idle lock measured in minutes, sampling once a second is
 * behaviourally identical.
 */
const ACTIVITY_THROTTLE_MS = 1000;

export const AppLockListener = ({ children }) => {
  const dispatch = useDispatch();
  const { isAppLocked, passcodeSettings } = useSelector((state) => state.AppReducer, shallowEqual);
  const { status, lockTime } = passcodeSettings || {};

  const timerRef = useRef(null);

  useEffect(() => {
    // Nothing to watch when the lock is off, or already engaged.
    if (!status || isAppLocked) return undefined;

    // `lockTime * 1000` on an absent value produced NaN, and setTimeout coerces
    // NaN to 0 — which locked the app instantly, over and over.
    const seconds = Number(lockTime);
    const delay = (Number.isFinite(seconds) && seconds > 0 ? seconds : DEFAULT_LOCK_SECONDS) * 1000;

    const arm = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => dispatch(AppAction.AppLockTrigger()), delay);
    };

    let lastSignal = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastSignal < ACTIVITY_THROTTLE_MS) return;
      lastSignal = now;
      arm();
    };

    // Passive: these are read-only observers and must never block scrolling.
    ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, onActivity, { passive: true }));
    arm();

    return () => {
      ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, onActivity));
      clearTimeout(timerRef.current);
    };
  }, [isAppLocked, status, lockTime, dispatch]);

  return (
    <>
      {isAppLocked && status && <AppLockModal />}
      {children}
    </>
  );
};
